import React, { useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { LatLngBounds, LatLng, Marker as LeafletMarker } from 'leaflet';
import { Tag, Typography, Button, Space } from 'antd';
import { Ambassador, AmbassadorCluster } from '../../types';
import { useGeographicData } from '../../hooks/useAmbassadors';
import { useFilterActions } from '../../store/useFilters';
import { arteliaColors } from '../../theme';
import { getIcon, clearIconCache } from '../../utils/iconCache';
import { createAmbassadorClusters, createClusterIcon } from '../../utils/mapClustering';

const { Text, Title } = Typography;

interface AmbassadorMapProps {
  ambassadors: Ambassador[];
  selectedAmbassador?: Ambassador | null;
  onAmbassadorSelect?: (ambassador: Ambassador) => void;
}

// Component to handle map bounds updates
const MapBoundsHandler: React.FC<{ bounds: LatLngBounds }> = ({ bounds }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
};

// Cluster marker component
const ClusterMarker = React.memo<{
  cluster: AmbassadorCluster;
  isSelected: boolean;
  onLocationFilter: (ambassadors: Ambassador[]) => void;
  onAmbassadorSelect: (ambassador: Ambassador) => void;
}>(({ cluster, isSelected, onLocationFilter, onAmbassadorSelect }) => {
  const handleClick = useCallback(() => {
    if (cluster.isCluster) {
      // Multiple ambassadors - trigger location filter
      onLocationFilter(cluster.ambassadors);
    } else {
      // Single ambassador - show popup/modal (handled by popup click)
      // The popup will handle individual selection
    }
  }, [cluster, onLocationFilter]);

  return (
    <Marker
      position={cluster.position}
      icon={cluster.isCluster ? createClusterIcon(cluster) : getIcon(cluster.ambassadors[0].role, isSelected)}
      eventHandlers={{
        click: handleClick,
      }}
    >
      <Popup>
        {cluster.isCluster ? (
          // Cluster popup
          <div style={{ minWidth: '250px' }}>
            <div style={{ marginBottom: '12px' }}>
              <Title level={5} style={{ margin: 0, color: arteliaColors.bluePrimary }}>
                {cluster.ambassadors.length} Ambassadors
              </Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {cluster.city}, {cluster.country}
              </Text>
            </div>
            
            <div style={{ marginBottom: '12px', maxHeight: '120px', overflowY: 'auto' }}>
              <Space wrap size={[4, 4]}>
                {cluster.ambassadors.map((ambassador) => (
                  <Tag 
                    key={ambassador.id}
                    color={ambassador.role === 'Booster' ? 'gold' : 'blue'}
                    style={{ margin: '2px', fontSize: '11px' }}
                  >
                    {ambassador.fullName}
                  </Tag>
                ))}
              </Space>
            </div>
            
            <Button 
              type="primary" 
              size="small" 
              block
              onClick={() => onLocationFilter(cluster.ambassadors)}
            >
              View {cluster.ambassadors.length} in Directory →
            </Button>
          </div>
        ) : (
          // Individual ambassador popup
          <div style={{ minWidth: '200px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Title level={5} style={{ margin: 0, color: arteliaColors.bluePrimary }}>
                {cluster.ambassadors[0].fullName}
              </Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {cluster.ambassadors[0].city}, {cluster.ambassadors[0].country}
              </Text>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <Tag 
                color={cluster.ambassadors[0].role === 'Booster' ? 'gold' : 'blue'}
                style={{ marginRight: '4px' }}
              >
                {cluster.ambassadors[0].role}
              </Tag>
              <Tag color="default">
                {cluster.ambassadors[0].businessUnit}
              </Tag>
            </div>
            
            <div>
              <Text style={{ fontSize: '12px' }}>
                <strong>Activity:</strong> {cluster.ambassadors[0].activity}
              </Text>
            </div>
            
            <div style={{ marginTop: '4px' }}>
              <Text style={{ fontSize: '12px' }}>
                <strong>Region:</strong> {cluster.ambassadors[0].region}
              </Text>
            </div>

            <div style={{ marginTop: '8px' }}>
              <Button 
                type="link" 
                size="small" 
                onClick={() => onAmbassadorSelect(cluster.ambassadors[0])}
                style={{ padding: 0, height: 'auto' }}
              >
                View Details →
              </Button>
            </div>
          </div>
        )}
      </Popup>
    </Marker>
  );
});


const AmbassadorMap: React.FC<AmbassadorMapProps> = ({
  ambassadors,
  selectedAmbassador,
  onAmbassadorSelect,
}) => {
  const mapRef = useRef<any>(null);
  const { bounds } = useGeographicData(ambassadors);
  const { setLocationFilter } = useFilterActions();

  // Cleanup icon cache on unmount
  useEffect(() => {
    return () => {
      clearIconCache();
    };
  }, []);

  // Create ambassador clusters
  const clusters = useMemo(() => 
    createAmbassadorClusters(ambassadors, 0.001),
    [ambassadors]
  );

  // Handle location filtering
  const handleLocationFilter = useCallback((clusterAmbassadors: Ambassador[]) => {
    const ambassadorIds = clusterAmbassadors.map(a => a.id);
    setLocationFilter(ambassadorIds);
  }, [setLocationFilter]);

  // Convert bounds to Leaflet format
  const leafletBounds = useMemo(() => {
    return new LatLngBounds(
      [bounds.south, bounds.west],
      [bounds.north, bounds.east]
    );
  }, [bounds]);

  // Default center of Europe
  const defaultCenter: [number, number] = [50.0755, 4.4357];

  // Memoize cluster markers array to prevent unnecessary re-renders
  const clusterMarkers = useMemo(() => 
    clusters.map((cluster, index) => {
      // Check if any ambassador in the cluster is selected
      const isSelected = cluster.ambassadors.some(ambassador => 
        selectedAmbassador?.id === ambassador.id
      );
      
      return (
        <ClusterMarker
          key={`cluster-${index}-${cluster.ambassadors.map(a => a.id).join('-')}`}
          cluster={cluster}
          isSelected={isSelected}
          onLocationFilter={handleLocationFilter}
          onAmbassadorSelect={onAmbassadorSelect || (() => {})}
        />
      );
    }),
    [clusters, selectedAmbassador, handleLocationFilter, onAmbassadorSelect]
  );

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        maxZoom={18}
        minZoom={2}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsHandler bounds={leafletBounds} />

        {clusterMarkers}
      </MapContainer>
      
      {/* Map Legend */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        background: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        fontSize: '12px',
        zIndex: 1000,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            background: arteliaColors.bluePrimary,
            borderRadius: '50%',
            border: '2px solid white',
          }} />
          <Text style={{ fontSize: '11px' }}>Key Users</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            background: arteliaColors.greenDarker,
            borderRadius: '50%',
            border: '2px solid white',
          }} />
          <Text style={{ fontSize: '11px' }}>Boosters</Text>
        </div>
      </div>
    </div>
  );
};

// Memoize AmbassadorMap to prevent unnecessary re-renders
export default memo(AmbassadorMap);